import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { billingApi, type BillingStatus } from '@/lib/api';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Sparkles,
  FileText,
  Users,
  Package,
  CreditCard,
  Bell,
  BarChart3,
  Smartphone,
  Loader2,
  AlertCircle,
  Calendar,
  Mail,
  ExternalLink,
} from 'lucide-react';
import {
  pricingPlans,
  includedInEveryPlan,
  customPlanFeatures,
  additionalSeatPrice,
  type PricingPlan,
  type IncludedFeatureCategory,
} from '@/data/pricingPlans';
import { cn } from '@/lib/utils';
import { CancelSubscriptionDialog } from '@/components/Modals/CancelSubscriptionDialog';

// Icon mapping for dynamic rendering
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Users,
  Package,
  CreditCard,
  Bell,
  BarChart3,
  Smartphone,
};

function PlanCard({
  plan,
  isAnnual,
  isCurrentPlan,
  isLoading,
  hasActiveSubscription,
  onSelectPlan,
}: {
  plan: PricingPlan;
  isAnnual: boolean;
  isCurrentPlan: boolean;
  isLoading: boolean;
  hasActiveSubscription: boolean;
  onSelectPlan: (planTier: string) => void;
}) {
  const price = isAnnual ? plan.pricing.annually : plan.pricing.monthly;
  // Only show "Most Popular" highlight if user doesn't have an active subscription
  const showPopular = plan.isPopular && !hasActiveSubscription;

  return (
    <Card
      className={cn(
        'relative flex flex-col',
        showPopular && 'border-primary shadow-lg scale-[1.02] lg:scale-105',
        isCurrentPlan && 'ring-2 ring-primary'
      )}
    >
      {showPopular && (
        <div className="absolute -top-3 left-0 right-0 flex justify-center">
          <Badge className="shadow-sm">
            Most Popular
          </Badge>
        </div>
      )}
      {isCurrentPlan && (
        <div className="absolute -top-3 left-0 right-0 flex justify-center">
          <Badge variant="secondary" className="shadow-sm">
            Current Plan
          </Badge>
        </div>
      )}

      <CardHeader className={cn('text-center pb-2', (showPopular || isCurrentPlan) && 'pt-8')}>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription className="min-h-[40px]">
          {plan.tier === 'base' && 'Essential tools to get started'}
          {plan.tier === 'premium' && 'Everything you need to grow'}
          {plan.tier === 'premium_plus' && 'Advanced features for power users'}
          {plan.tier === 'unlimited_owl' && 'For teams that want it all'}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 pt-4">
        <div className="text-center mb-6">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-bold tracking-tight">${price}</span>
            <span className="text-muted-foreground">/month</span>
          </div>
          {isAnnual ? (
            <p className="text-sm text-muted-foreground mt-1">
              billed annually
            </p>
          ) : (
            <p className="text-sm text-primary mt-1">
              ${plan.pricing.annually}/mo if billed annually
            </p>
          )}
        </div>

        <Separator className="my-6" />

        <ul className="space-y-3">
          {plan.cumulativeFeatures?.map((feature, index) => (
            <li key={`cumulative-${index}`} className="flex items-start gap-3">
              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{feature}</span>
            </li>
          ))}
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="pt-6">
        <Button
          className="w-full h-11"
          variant={isCurrentPlan ? 'secondary' : showPopular ? 'default' : 'outline'}
          onClick={() => onSelectPlan(plan.tier)}
          disabled={isCurrentPlan || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : showPopular ? (
            'Get Started'
          ) : (
            'Select Plan'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

function FeatureCategory({ category }: { category: IncludedFeatureCategory }) {
  const Icon = iconMap[category.icon] || FileText;

  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <h4 className="font-medium text-sm mb-1">{category.category}</h4>
        <p className="text-sm text-muted-foreground">
          {category.items.join(' · ')}
        </p>
      </div>
    </div>
  );
}

// Map API plan names to pricing plan tiers
const planTierMap: Record<string, string> = {
  'free_trial': 'base',
  'base': 'base',
  'premium': 'premium',
  'premium_plus': 'premium_plus',
  'unlimited': 'unlimited_owl',
};

// Reverse map: frontend tier to API plan key (for checkout)
const tierToPlanMap: Record<string, string> = {
  'base': 'base',
  'premium': 'premium',
  'premium_plus': 'premium_plus',
  'unlimited_owl': 'unlimited',
};

export function BillingSettings() {
  const { nav, businessId } = useBusinessNav();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null); // null or plan tier being loaded
  const [error, setError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const isAnnual = billingCycle === 'annual';

  useEffect(() => {
    loadBillingStatus();
  }, [businessId]);

  const loadBillingStatus = async () => {
    setLoading(true);
    const response = await billingApi.getStatus(businessId || undefined);
    if (response.data) {
      setBillingStatus(response.data);
    } else if (response.error) {
      setError(response.error);
    }
    setLoading(false);
  };

  const handleManageSubscription = async () => {
    setActionLoading('portal');
    setError('');
    const response = await billingApi.createPortalSession(businessId || '');
    if (response.data?.portal_url) {
      window.location.href = response.data.portal_url;
    } else if (response.error) {
      setError(response.error);
      setActionLoading(null);
    }
  };

  const handleSelectPlan = async (planTier: string) => {
    // If user already has a Stripe subscription, use portal to change plans
    if (billingStatus?.has_stripe_subscription) {
      handleManageSubscription();
      return;
    }

    // Otherwise, use checkout to subscribe
    const apiPlan = tierToPlanMap[planTier];
    if (!apiPlan) {
      setError('Invalid plan selected');
      return;
    }

    setActionLoading(planTier);
    setError('');
    const response = await billingApi.createCheckoutSession(apiPlan, isAnnual ? 'annual' : 'monthly', businessId || '');
    if (response.data?.checkout_url) {
      window.location.href = response.data.checkout_url;
    } else if (response.error) {
      setError(response.error);
      setActionLoading(null);
    }
  };

  // Find current plan from pricing plans (only highlight if actively subscribed, not on free trial)
  const currentPlanTier = billingStatus?.has_stripe_subscription && billingStatus?.plan
    ? planTierMap[billingStatus.plan]
    : null;

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 pt-6 pb-12">
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Non-owner view - show simplified billing info */}
      {billingStatus && !billingStatus.is_billing_owner && (
        <div className="max-w-md mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav('/settings')}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </Button>

          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-2">
                <CreditCard className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardTitle>Billing</CardTitle>
              <CardDescription>
                View your current plan details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Current Plan</p>
                <p className="text-xl font-semibold">
                  {billingStatus.plan_display_name || 'No Plan'}
                </p>
              </div>

              <Separator />

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  To change plans or manage billing, please contact your account administrator:
                </p>
                {billingStatus.billing_admin_email && (
                  <a
                    href={`mailto:${billingStatus.billing_admin_email}`}
                    className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                  >
                    <Mail className="h-4 w-4" />
                    {billingStatus.billing_admin_email}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Owner view - full billing management */}
      {billingStatus?.is_billing_owner && (
        <>
          {/* Header - only show for active subscribers */}
          {billingStatus?.has_stripe_subscription && (
            <div className="mb-10">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav('/settings')}
                className="mb-4 -ml-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Settings
              </Button>
              <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
              <p className="text-muted-foreground mt-1">
                Manage your subscription and plan.
              </p>
            </div>
          )}

          {/* App Store Subscriber Card - managed via Apple */}
          {billingStatus?.subscription_source === 'app_store' && (
            <Card className="mb-10">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-sm text-muted-foreground">Current Plan</p>
                      <p className="font-semibold">
                        {billingStatus?.plan_display_name || 'Unknown'}
                        <Badge variant="outline" className="ml-2">App Store</Badge>
                        {billingStatus?.cancel_at_period_end && (
                          <Badge variant="destructive" className="ml-2">Canceling</Badge>
                        )}
                      </p>
                      {billingStatus?.current_period_end && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Calendar className="h-3 w-3" />
                          {billingStatus.cancel_at_period_end ? 'Ends' : 'Renews'} {formatDate(billingStatus.current_period_end)}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground mt-2">
                        Your subscription is managed through the App Store.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    asChild
                  >
                    <a
                      href="https://apps.apple.com/account/subscriptions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Manage in App Store
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Play Store Subscriber Card - active, managed via Google Play */}
          {billingStatus?.subscription_source === 'play_store' && billingStatus?.is_active && (
            <Card className="mb-10">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-sm text-muted-foreground">Current Plan</p>
                      <p className="font-semibold">
                        {billingStatus?.plan_display_name || 'Unknown'}
                        <Badge variant="outline" className="ml-2">Google Play</Badge>
                        {billingStatus?.cancel_at_period_end && (
                          <Badge variant="destructive" className="ml-2">Canceling</Badge>
                        )}
                      </p>
                      {billingStatus?.current_period_end && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Calendar className="h-3 w-3" />
                          {billingStatus.cancel_at_period_end ? 'Ends' : 'Renews'} {formatDate(billingStatus.current_period_end)}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground mt-2">
                        Your subscription is managed through Google Play.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    asChild
                  >
                    <a
                      href="https://play.google.com/store/account/subscriptions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Manage in Google Play
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Play Store Subscriber Card - expired, prompt to switch to Stripe */}
          {billingStatus?.subscription_source === 'play_store' && !billingStatus?.is_active && (
            <Card className="mb-10 border-amber-300 dark:border-amber-700">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-sm text-muted-foreground">Current Plan</p>
                      <p className="font-semibold">
                        {billingStatus?.plan_display_name || 'Unknown'}
                        <Badge variant="destructive" className="ml-2">Expired</Badge>
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Your Google Play subscription has expired. Choose a plan below to continue with direct billing.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Current Plan Card - only show for Stripe subscribers */}
          {billingStatus?.subscription_source === 'stripe' && billingStatus?.has_stripe_subscription && (
        <Card className="mb-10">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="font-semibold">
                    {billingStatus?.plan_display_name || 'Unknown'}
                    {billingStatus?.cancel_at_period_end && (
                      <Badge variant="destructive" className="ml-2">Canceling</Badge>
                    )}
                  </p>
                  {billingStatus?.current_period_end && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3" />
                      {billingStatus.cancel_at_period_end ? 'Ends' : 'Renews'} {formatDate(billingStatus.current_period_end)}
                    </p>
                  )}
                  {/* Seat usage */}
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Users className="h-3 w-3" />
                    {billingStatus.total_seats_used} seat{billingStatus.total_seats_used !== 1 ? 's' : ''}
                    {billingStatus.owned_businesses_count > 1 && ' across all businesses'}
                    {' '}({billingStatus.free_seats_included} free
                    {billingStatus.billable_seats > 0 && (
                      <span className="text-foreground">
                        {' '}+ {billingStatus.billable_seats} additional @ ${billingStatus.billable_seats * billingStatus.seat_price_monthly}/mo
                      </span>
                    )})
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={actionLoading === 'portal'}
                >
                  {actionLoading === 'portal' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Manage Subscription
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
                {!billingStatus?.cancel_at_period_end && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancel subscription
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onCancelled={loadBillingStatus}
      />


      {/* Legacy User Banner */}
      {billingStatus?.is_legacy && !billingStatus?.migrated_at && (
        <Card className={`mb-10 ${billingStatus?.is_active ? 'border-primary/50 bg-primary/5' : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20'}`}>
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${billingStatus?.is_active ? 'bg-primary/10' : 'bg-amber-100 dark:bg-amber-950'}`}>
                <Sparkles className={`h-5 w-5 ${billingStatus?.is_active ? 'text-primary' : 'text-amber-600 dark:text-amber-400'}`} />
              </div>
              <div>
                {billingStatus?.is_active ? (
                  <>
                    <p className="font-semibold mb-1">Grandfathered Premium Plan</p>
                    {billingStatus.subscription_source === 'stripe' ? (
                      <p className="text-sm text-muted-foreground">
                        Thank you for being a loyal customer! Your legacy pricing has been
                        grandfathered. You can manage your subscription or switch plans below.
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground mb-3">
                          Thank you for being a loyal customer! You have been grandfathered into a
                          Premium Plan through{' '}
                          <span className="font-medium text-foreground">January 1, 2027</span>.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          At any point you can cancel your{' '}
                          {billingStatus.subscription_source === 'app_store'
                            ? 'Apple App Store'
                            : 'Google Play Store'}{' '}
                          subscription and switch to a direct billing plan below.
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold mb-1">Subscription Expired</p>
                    <p className="text-sm text-muted-foreground">
                      Your Google Play subscription has expired. Choose a plan below to continue using InvoiceOwl with direct billing — no app store needed.
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(billingStatus?.subscription_source === 'stripe' ||
        (billingStatus?.is_legacy && !billingStatus?.migrated_at && !billingStatus?.is_active &&
         (billingStatus?.subscription_source === 'play_store' || billingStatus?.subscription_source === 'app_store'))) && (
        <>
          {/* Pricing Section Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold tracking-tight mb-3">
              Simple, transparent pricing
            </h2>
            <p className="text-muted-foreground mb-6">
              <span className="text-primary font-medium">Cancel anytime, no contract.</span>
            </p>

            {/* Billing Toggle */}
            <Tabs value={billingCycle} onValueChange={(v) => setBillingCycle(v as 'monthly' | 'annual')}>
              <TabsList>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="annual" className="gap-2">
                  Annual
                  <Badge variant="secondary" className="text-xs">
                    Save 35%
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {pricingPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isAnnual={isAnnual}
                isCurrentPlan={currentPlanTier === plan.tier}
                isLoading={actionLoading === plan.tier}
                hasActiveSubscription={!!billingStatus?.has_stripe_subscription}
                onSelectPlan={handleSelectPlan}
              />
            ))}
          </div>

          {/* Additional Seats */}
          <p className="text-center text-sm text-muted-foreground mb-10">
            <span className="font-medium text-foreground">${additionalSeatPrice.monthly}/mo</span>{' '}
            per additional team member on any plan
          </p>

          {/* Need More - Compact */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-6 px-6 rounded-xl border border-dashed bg-muted/30 mb-16">
            <div className="text-center sm:text-left">
              <span className="font-medium">Need more?</span>
              <span className="text-muted-foreground ml-2">
                {customPlanFeatures.map(f => f.feature).join(', ')} and more.
              </span>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href="mailto:support@invoiceowl.com">Contact Sales</a>
            </Button>
          </div>

          <Separator className="mb-12" />

          {/* Included in Every Plan - Better Layout */}
          <div>
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold tracking-tight mb-1">
                Included in every plan
              </h2>
              <p className="text-sm text-muted-foreground">
                All the essentials to run your business.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {includedInEveryPlan.map((category) => (
                <FeatureCategory key={category.category} category={category} />
              ))}
            </div>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}
